import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import './Admin.css';
import { StarIcon, TrashIcon } from '../../components/SvgIcons';
import AedPrice from '../../components/AedPrice';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [showBaseStockModal, setShowBaseStockModal] = useState(false);
  const [baseStockValues, setBaseStockValues] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteProductId, setDeleteProductId] = useState(null);
  const [deletePin, setDeletePin] = useState('');
  const [deletePinError, setDeletePinError] = useState('');
  const { token } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch('/api/products?includeHidden=true');
      const data = await response.json();
      setProducts(data);
      // Initialize base stock values with current stock
      const baseStocks = {};
      data.forEach(p => { baseStocks[p.id] = p.stock; });
      setBaseStockValues(baseStocks);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleProductEnabled = async (id) => {
    try {
      const response = await fetch(`/api/products/${id}/toggle`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchProducts();
      }
    } catch (error) {
      console.error('Failed to toggle product:', error);
    }
  };

  const openBaseStockModal = () => {
    // Use actual baseStock values from database
    const baseStocks = {};
    products.forEach(p => { baseStocks[p.id] = p.baseStock || p.stock; });
    setBaseStockValues(baseStocks);
    setShowBaseStockModal(true);
  };

  const handleRecalculateStock = async () => {
    setRecalculating(true);
    try {
      const response = await fetch('/api/products/recalculate-stock', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ baseStock: baseStockValues })
      });

      if (response.ok) {
        const result = await response.json();
        alert(isRTL
          ? `تم إعادة حساب المخزون لـ ${result.products.length} منتج`
          : `Stock recalculated for ${result.products.length} products`
        );
        fetchProducts();
        setShowBaseStockModal(false);
      }
    } catch (error) {
      console.error('Failed to recalculate stock:', error);
      alert(isRTL ? 'فشل في إعادة حساب المخزون' : 'Failed to recalculate stock');
    } finally {
      setRecalculating(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteProductId(id);
    setDeletePin('');
    setDeletePinError('');
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    // Check PIN (4444)
    if (deletePin !== '4444') {
      setDeletePinError(isRTL ? 'رمز PIN غير صحيح' : 'Incorrect PIN');
      return;
    }

    try {
      const response = await fetch(`/api/products/${deleteProductId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchProducts();
        setShowDeleteModal(false);
        setDeleteProductId(null);
        setDeletePin('');
      }
    } catch (error) {
      console.error('Failed to delete product:', error);
    }
  };

  const formatPrice = (price) => <AedPrice amount={price} />;

  const getProductName = (product) => {
    return isRTL && product.nameAr ? product.nameAr : product.name;
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-page ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container">
        <div className="admin-card" style={{ padding: '12px 16px', marginBottom: '24px', ...(window.innerWidth >= 768 ? { position: 'sticky', top: '72px', zIndex: 99 } : {}) }}>
          <div className="admin-nav" style={{ marginBottom: 0 }}>
            <Link to="/dashboard4">{t('dashboard')}</Link>
            <Link to="/dashboard4/products" className="active">{t('products')}</Link>
            <Link to="/dashboard4/orders">{t('orders')}</Link>
            <Link to="/dashboard4/create-order">{isRTL ? 'إنشاء طلب' : 'Create Order'}</Link>
            <Link to="/dashboard4/inventory">{isRTL ? "المخزون" : "Inventory"}</Link>
            <Link to="/dashboard4/stats">{isRTL ? "الإحصائيات" : "Stats"}</Link>
            <Link to="/dashboard4/settings">{isRTL ? 'الإعدادات' : 'Settings'}</Link>
          </div>
        </div>

        <div className="admin-card products-toolbar-card" style={{ padding: '16px 20px', marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <div>
            <div style={{ fontWeight: '800', fontSize: '28px', color: '#1e293b', lineHeight: 1 }}>{products.length}</div>
            <div style={{ fontWeight: '600', fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>{t('items')}</div>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={openBaseStockModal}
              disabled={recalculating}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px 14px', borderRadius: '25px' }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 4v6h-6M1 20v-6h6"/>
                <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
              </svg>
              <span className="btn-label-text" style={{ fontWeight: '700', fontSize: '14px', color: '#1e293b' }}>{recalculating
                ? (isRTL ? 'جاري الحساب' : 'Calculating')
                : (isRTL ? 'إعادة حساب المخزون' : 'Recalculate Stock')
              }</span>
            </button>
            <button onClick={() => navigate('/dashboard4/products/new/edit')} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px 14px', borderRadius: '25px' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="16"/>
                <line x1="8" y1="12" x2="16" y2="12"/>
              </svg>
              <span className="btn-label-text" style={{ fontWeight: '700', fontSize: '14px', color: '#1e293b' }}>{t('addProduct')}</span>
            </button>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="products-table desktop-only">
          <table>
            <thead>
              <tr>
                <th>{t('imageUrl')}</th>
                <th>{t('productName')}</th>
                <th>{t('category')}</th>
                <th>{t('price')}</th>
                <th>{t('stock')}</th>
                <th>{t('featured')}</th>
                <th>{isRTL ? 'الحالة' : 'Status'}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {products.map(product => (
                <tr key={product.id}>
                  <td>
                    <div className="product-thumb">
                      <img src={product.image} alt={getProductName(product)} />
                    </div>
                  </td>
                  <td>{getProductName(product)}</td>
                  <td>{product.category}</td>
                  <td>
                    {product.onSale && product.salePrice ? (
                      <span>
                        <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '12px' }}>{formatPrice(product.price)}</span>
                        <br />
                        <span style={{ color: '#dc2626', fontWeight: '700' }}>{formatPrice(product.salePrice)}</span>
                        <span style={{ fontSize: '10px', background: '#fef3c7', color: '#d97706', padding: '1px 5px', borderRadius: '4px', marginLeft: '4px' }}>SALE</span>
                      </span>
                    ) : formatPrice(product.price)}
                  </td>
                  <td>{product.stock}{product.baseStock > 0 && <span style={{ color: '#9ca3af', fontSize: '12px' }}> / {product.baseStock}</span>}</td>
                  <td>{product.featured ? '✓' : '-'}</td>
                  <td>
                    <button
                      onClick={() => toggleProductEnabled(product.id)}
                      className={`btn btn-sm ${product.enabled !== false ? 'btn-success' : 'btn-secondary'}`}
                      style={{
                        minWidth: '90px',
                        background: product.enabled !== false ? '#10b981' : '#9ca3af',
                        color: 'white',
                        border: 'none'
                      }}
                    >
                      {product.enabled !== false
                        ? (isRTL ? 'متاح' : 'Enabled')
                        : (isRTL ? 'معطل' : 'Disabled')
                      }
                    </button>
                  </td>
                  <td>
                    <div className="action-btns">
                      <button
                        onClick={() => navigate(`/dashboard4/products/${product.id}/edit`)}
                        className="btn btn-sm btn-outline"
                      >
                        {t('edit')}
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="delete-btn"
                        title={t('deleteProduct')}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="products-mobile mobile-only">
          {products.map(product => (
            <div key={product.id} className="product-card-mobile">
              <div className="product-card-header">
                <img
                  src={product.image}
                  alt={getProductName(product)}
                  className="product-card-image"
                />
                <div className="product-card-info">
                  <div className="product-card-name">{getProductName(product)}</div>
                  <div className="product-card-price">
                    {product.onSale && product.salePrice ? (
                      <>
                        <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '12px', marginRight: '6px' }}>{formatPrice(product.price)}</span>
                        <span style={{ color: '#dc2626', fontWeight: '700' }}>{formatPrice(product.salePrice)}</span>
                      </>
                    ) : formatPrice(product.price)}
                  </div>
                </div>
              </div>

              <div className="product-card-capsules">
                <span className="capsule capsule-category">
                  {isRTL && product.categoryAr ? product.categoryAr : product.category}
                </span>
                <span className={`capsule ${product.stock > 10 ? 'capsule-stock' : product.stock > 0 ? 'capsule-stock-low' : 'capsule-stock-out'}`}>
                  {isRTL ? `المخزون: ${product.stock}` : `Stock: ${product.stock}`}{product.baseStock > 0 && ` / ${product.baseStock}`}
                </span>
                {product.featured && (
                  <span className="capsule capsule-featured">
                    <StarIcon size="12px" style={{ marginRight: '4px' }} /> {isRTL ? 'مميز' : 'Featured'}
                  </span>
                )}
                <span
                  className={`capsule ${product.enabled !== false ? 'capsule-enabled' : 'capsule-disabled'}`}
                  onClick={() => toggleProductEnabled(product.id)}
                  style={{ cursor: 'pointer' }}
                >
                  {product.enabled !== false ? (isRTL ? '✓ متاح' : '✓ Enabled') : (isRTL ? '✗ معطل' : '✗ Disabled')}
                </span>
              </div>

              <div className="product-card-actions">
                <button
                  onClick={() => navigate(`/dashboard4/products/${product.id}/edit`)}
                  className="btn btn-outline"
                >
                  {t('edit')}
                </button>
                <button
                  onClick={() => handleDelete(product.id)}
                  className="delete-btn"
                  title={t('deleteProduct')}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z"/>
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Base Stock Modal */}
        {showBaseStockModal && (
          <div className="modal-overlay" onClick={() => setShowBaseStockModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} dir={isRTL ? 'rtl' : 'ltr'}>
              <h2>{isRTL ? 'المخزون الأساسي' : 'Base Stock'}</h2>
              <p style={{ color: 'var(--gray-600)', marginBottom: '20px', fontSize: '14px' }}>
                {isRTL
                  ? 'أدخل المخزون الأساسي (الأصلي) لكل منتج. سيتم حساب المخزون الحالي تلقائياً: الأساسي − المباع.'
                  : 'Set the original base stock for each product. Current stock will be calculated: Base − Sold.'}
              </p>

              {products.map(product => {
                const base = baseStockValues[product.id] || 0;
                const sold = Math.max(0, (product.baseStock || 0) - product.stock);
                return (
                  <div key={product.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    background: 'var(--gray-50)',
                    borderRadius: '10px',
                    marginBottom: '8px'
                  }}>
                    <img
                      src={product.image}
                      alt={getProductName(product)}
                      style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '8px' }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ fontSize: '14px' }}>{getProductName(product)}</strong>
                      <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px' }}>
                        {isRTL ? 'الحالي:' : 'Now:'} {product.stock} · {isRTL ? 'مباع:' : 'Sold:'} {sold}
                      </div>
                    </div>
                    <input
                      type="number"
                      value={base}
                      onChange={(e) => setBaseStockValues(prev => ({
                        ...prev,
                        [product.id]: parseInt(e.target.value) || 0
                      }))}
                      className="form-input"
                      style={{ width: '80px', padding: '8px', fontSize: '16px', fontWeight: '700', textAlign: 'center', borderRadius: '10px' }}
                      min="0"
                    />
                  </div>
                );
              })}

              <div className="modal-actions" style={{ marginTop: '20px' }}>
                <button
                  type="button"
                  onClick={() => setShowBaseStockModal(false)}
                  className="btn btn-secondary"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleRecalculateStock}
                  className="btn btn-primary"
                  disabled={recalculating}
                >
                  {recalculating
                    ? (isRTL ? 'جاري الحفظ...' : 'Saving...')
                    : (isRTL ? 'حفظ وحساب المخزون' : 'Save & Recalculate')
                  }
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal with PIN */}
        {showDeleteModal && (
          <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} dir={isRTL ? 'rtl' : 'ltr'} style={{ maxWidth: '400px' }}>
              <h2 style={{ color: '#dc2626', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}><TrashIcon size="22px" /> {isRTL ? 'حذف المنتج' : 'Delete Product'}</h2>
              <p style={{ color: 'var(--gray-600)', marginBottom: '20px' }}>
                {isRTL
                  ? 'أدخل رمز PIN للإعدادات لتأكيد الحذف'
                  : 'Enter Settings PIN to confirm deletion'
                }
              </p>

              <div className="form-group">
                <label className="form-label">{isRTL ? 'رمز PIN' : 'PIN Code'}</label>
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={deletePin}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    setDeletePin(val);
                    setDeletePinError('');
                  }}
                  className="form-input"
                  placeholder="••••"
                  maxLength={4}
                  style={{ textAlign: 'center', fontSize: '24px', letterSpacing: '8px' }}
                  autoFocus
                  autoComplete="off"
                  data-lpignore="true"
                  data-form-type="other"
                  onKeyDown={(e) => e.key === 'Enter' && confirmDelete()}
                />
                {deletePinError && (
                  <p style={{ color: '#dc2626', fontSize: '13px', marginTop: '8px' }}>{deletePinError}</p>
                )}
              </div>

              <div className="modal-actions" style={{ marginTop: '20px' }}>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="btn btn-secondary"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={confirmDelete}
                  className="btn"
                  style={{ background: '#dc2626', color: 'white' }}
                >
                  {isRTL ? 'حذف' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
